import { lazy, memo, Suspense, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Loading from '../components/layouts/Loading';
import { AuthContext } from '../contexts/AuthContext';
import { getProductComments } from '../redux/actions/comments';
import { getUserOrders } from '../redux/actions/orders';
import {
  getProductDetail,
  getRelatedProducts,
} from '../redux/actions/products';
import { comments$, orders$, products$ } from '../redux/selectors';

const Comments = lazy(() => import('../components/productDetail/Comments'));
const Detail = lazy(() => import('../components/productDetail/Detail'));
const Header = lazy(() => import('../components/productDetail/Header'));
const Related = lazy(() => import('../components/productDetail/Related'));

const ProductDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch(getProductDetail);
  const products = useSelector(products$);
  const comments = useSelector(comments$);
  const orders = useSelector(orders$);
  const {
    authState: { isAuthenticated },
  } = useContext(AuthContext);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getProductDetail.getProductDetailRequest(id));
    if (
      products?.singleProduct?.categoryId?.id !== undefined &&
      products?.singleProduct?.id !== undefined
    ) {
      const reviewDetail = new DOMParser().parseFromString(
        products?.singleProduct?.review,
        'text/html'
      ).body;
      const reviewDetailParentNode = document.getElementById('review-detail');
      if (
        reviewDetailParentNode?.childNodes[0]?.nodeValue === undefined &&
        reviewDetail?.childNodes[0]?.nodeValue !== 'undefined'
      ) {
        reviewDetailParentNode.appendChild(reviewDetail);
      }
      dispatch(
        getRelatedProducts.getRelatedProductsRequest(
          products?.singleProduct?.categoryId?.id
        )
      );
      dispatch(
        getProductComments.getProductCommentsRequest(
          products?.singleProduct?.id
        )
      );
    }
    if (isAuthenticated && orders.userOrders.length === 0) {
      dispatch(getUserOrders.getUserOrdersRequest());
    }

    return () => {
      const reviewDetail = new DOMParser().parseFromString(
        products?.singleProduct?.review,
        'text/html'
      ).body;
      const reviewDetailParentNode = document.getElementById('review-detail');
      if (
        !(
          reviewDetailParentNode?.childNodes[0]?.nodeValue === undefined &&
          reviewDetail?.childNodes[0]?.nodeValue !== 'undefined'
        ) &&
        reviewDetailParentNode?.childNodes[0] !== undefined
      ) {
        reviewDetailParentNode?.removeChild(
          reviewDetailParentNode.childNodes[0]
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, products?.singleProduct?.id, isAuthenticated, comments.data.length]);

  return (
    <>
      {/* <!-- Breadcrumb Section Begin --> */}
      <Suspense fallback={<Loading />}>
        <Header products={products} />
      </Suspense>
      {/* <!-- Breadcrumb Section End --> */}

      {/* <!-- Product Details Section Begin --> */}
      <section
        className="product-details spad"
        style={{ paddingBottom: '50px' }}
      >
        <div className="container">
          <div className="row">
            <Suspense fallback={<Loading />}>
              <Detail products={products} comments={comments} />
            </Suspense>
            <div className="col-lg-12">
              <div className="product__details__tab">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item">
                    <a
                      className="nav-link active"
                      data-toggle="tab"
                      href="#tabs-3"
                      role="tab"
                      aria-selected="true"
                    >
                      {t(`productDetail.detail`)}
                    </a>
                  </li>
                </ul>
                <div id="review-detail"></div>
              </div>
            </div>
            <div className="col-lg-12">
              <div className="product__details__tab">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item">
                    <a
                      className="nav-link active"
                      data-toggle="tab"
                      href="#tabs-3"
                      role="tab"
                      aria-selected="true"
                    >
                      {t(`productDetail.postReview`)}{' '}
                      <span>({comments.data.length})</span>
                    </a>
                  </li>
                </ul>
                <Suspense fallback={<Loading />}>
                  <Comments
                    comments={comments}
                    orders={orders}
                    products={products}
                  />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <!-- Product Details Section End --> */}

      {/* <!-- Related Product Section Begin --> */}
      <Suspense fallback={<Loading />}>
        <Related products={products} />
      </Suspense>
      {/* <!-- Related Product Section End --> */}
    </>
  );
};

export default memo(ProductDetail);
